import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaHome, FaHeart, FaSearch, FaStar, FaEye, FaUserFriends, FaEnvelope, FaPhone, FaStore,
  FaSun, FaGem, FaCog, FaQuestionCircle, FaLock, FaSignOutAlt, FaBell, FaBars, FaFilter,
  FaUser, FaRobot, FaCheckCircle, FaCamera, FaCircle, FaGift, FaTrash, FaTimes
} from 'react-icons/fa';
import Home from '../components/dashboard/Home';
import Matches from '../components/dashboard/Matches';
import Search from '../components/dashboard/Search';
import Offers from '../components/dashboard/Offers';
import Messages from '../components/dashboard/Messages';
import Subscription from '../components/dashboard/Subscription';
import Settings from '../components/dashboard/Settings';
import Profile from '../components/dashboard/Profile';
import ProfileDetails from '../components/dashboard/ProfileDetails';
import Shortlisted from '../components/dashboard/Shortlisted';
import ViewedProfiles from '../components/dashboard/ViewedProfiles';
import Visitors from '../components/dashboard/Visitors';
import ContactReveals from '../components/dashboard/ContactReveals';
import HoroscopeMatch from '../components/dashboard/HoroscopeMatch';
// import Events from '../components/dashboard/Events';
import Help from '../components/dashboard/Help';
import DashboardPrivacy from '../components/dashboard/DashboardPrivacy';
import Market from '../components/dashboard/Market';
import AIJathagam from '../components/dashboard/AIJathagam';
import api from '../services/api';
import useSocket from "../components/dashboard/socketService";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [conversationToOpenId, setConversationToOpenId] = useState(null);
  const [features, setFeatures] = useState({});
  const [filter, setFilter] = useState("all");

  const isMessagesScreenActiveRef = useRef(false);


  // Profile Data State
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [stats, setStats] = useState({
    profileViews: 0,
    shortlisted: 0,
    matches: 0,
    messages: 0
  });

  // Notifications State
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [searchFilters, setSearchFilters] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = storedUser?._id || storedUser?.id || null;
  // NEW: Unread message count for Chat icon badge
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // GLOBAL NOTIFICATION SOUND REF
  const notificationAudioRef = useRef(null);
  // Track which notifications already played sound
  const notifiedIdsRef = useRef(new Set());
  const { forceDisconnect } = useSocket(userId); // or get from context if needed

  const audioUnlockedRef = useRef(false);
  const activeConversationRef = useRef(null);

  const unlockAudio = () => {
    if (audioUnlockedRef.current) return;

    const audio = notificationAudioRef.current;
    if (!audio) return;

    audio.muted = true;      // 🔕 mute first
    audio.volume = 0;
    audio.play().then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;  // 🔊 unmute AFTER unlock
      audio.volume = 1;
      audioUnlockedRef.current = true;
      console.log("🔓 Audio unlocked silently");
    }).catch(() => { });

    document.removeEventListener("click", unlockAudio);
    document.removeEventListener("keydown", unlockAudio);
    document.removeEventListener("touchstart", unlockAudio);
  };


  // GLOBAL SOCKET FOR NOTIFICATION SOUND ONLY
  useSocket(userId, {
    onNewMessage: ({ conversationId, message }) => {
      const isOwnMessage = String(message.sender) === String(userId);
      if (isOwnMessage) return;

      // 🚫 Messages screen is active → NEVER play sound
      if (isMessagesScreenActiveRef.current) {
        return;
      }

      // ✅ Play sound only when user is outside chat screen
      if (audioUnlockedRef.current && notificationAudioRef.current) {
        const audio = notificationAudioRef.current;
        audio.pause();
        audio.currentTime = 0;
        audio.play().catch(() => { });
      }

      setUnreadMessageCount(prev => prev + 1);
    },



    onNewNotification: (notification) => {
      // 🔔 ensure unread
      notification.isRead = false;

      // 🔊 play sound ONLY if audio unlocked and not already played
      if (!notifiedIdsRef.current.has(notification._id)) {
        notifiedIdsRef.current.add(notification._id);

        if (audioUnlockedRef.current && notificationAudioRef.current) {
          const audio = notificationAudioRef.current;
          audio.pause();
          audio.currentTime = 0;
          audio.play().catch(() => { });
        }
      }

      setNotifications(prev => {
        if (prev.some(n => n._id === notification._id)) return prev;
        return [notification, ...prev];
      });
    }
  });

  useEffect(() => {
    const handler = (e) => {
      activeConversationRef.current = e.detail || null;
    };



    window.addEventListener("active-conversation", handler);
    return () => window.removeEventListener("active-conversation", handler);
  }, []);


  useEffect(() => {
    const unlockAudioHandler = () => unlockAudio();

    document.addEventListener("click", unlockAudioHandler);
    document.addEventListener("keydown", unlockAudioHandler);
    document.addEventListener("touchstart", unlockAudioHandler);

    return () => {
      document.removeEventListener("click", unlockAudioHandler);
      document.removeEventListener("keydown", unlockAudioHandler);
      document.removeEventListener("touchstart", unlockAudioHandler);
    };
  }, [unreadMessageCount, notifications]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchProfileData();
    fetchNotifications();
  }, []);

  useEffect(() => {
    isMessagesScreenActiveRef.current = activeTab === "messages";
  }, [activeTab]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the notification dropdown
      const notificationDropdown = document.querySelector('.notification-dropdown-container');
      const notificationButton = document.querySelector('.notification-bell-button');

      if (
        notificationOpen &&
        notificationDropdown &&
        !notificationDropdown.contains(event.target) &&
        notificationButton &&
        !notificationButton.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (notificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup: remove listener when dropdown closes or component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationOpen]); // Only re-run if notificationOpen changes

  const fetchProfileData = async () => {
    try {
      const response = await api.get('/dashboard/my-profile');
      setUser(response.data.user);
      setProfile(response.data.profile);
      setFeatures(response.data.subscription?.planDetails?.features || {});
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response && (error.response.status === 404 || error.response.status === 401)) {
        handleLogout();
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get("/getall");
      setNotifications(res.data.data || []);
      setLoadingNotifications(false);
    } catch (err) {
      console.error("Error fetching notifications", err);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await api.delete("/clear-all");

      setNotifications([]);
      notifiedIdsRef.current.clear();
      setNotificationOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/${notificationId}/read`);

      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId ? { ...n, isRead: true } : n
        )
      );
    } catch (error) {
      console.error("Error marking notification as read", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post("/mark-all-read");

      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    window.dispatchEvent(new Event("app-logout"));
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    forceDisconnect?.();

    navigate("/login");
  };
  const handleViewProfile = (profileId) => {
    setSelectedProfileId(profileId);
    setActiveTab('view-profile');
  };


  const handleSearch = (filters) => {
    setFilter('search');          // 🔑 THIS IS REQUIRED
    setSearchFilters(filters);
    setActiveTab('search_results');
  };

  const openConversationAndMessages = (conversationId = null) => {
    activeConversationRef.current = conversationId;
    setConversationToOpenId(conversationId);
    setActiveTab('messages');
  };

  const FEATURE_TABS = {
    horoscope: "horoscopeMatching",
    "ai-jathagam": "aiJathagam",
    market: "marketAccess",
  };
  const handleFeatureClick = (tab) => {
    const requiredFeature = FEATURE_TABS[tab];

    if (requiredFeature && !features[requiredFeature]) {
      setActiveTab("subscription"); // 🔥 redirect
      return;
    }

    setActiveTab(tab);
    setSidebarOpen(false);
  };


  const renderContent = () => {
    switch (activeTab) {
      case 'home': return <Home setActiveTab={setActiveTab} />;
      case 'matches':
        return (
          <Matches
            filter={filter}
            searchQuery={searchQuery}
            onViewProfile={handleViewProfile}
            onOpenConversation={openConversationAndMessages}
            onUpgradeClick={() => setActiveTab('subscription')}
          />
        );
      case 'search_results':
        return (
          <Matches
            filter={filter}
            searchFilters={searchFilters}
            onViewProfile={handleViewProfile}
            onOpenConversation={openConversationAndMessages}
          />
        );
      case 'offers': return <Offers onBack={() => setActiveTab('home')} />;
      case 'view-profile':
        return (
          <ProfileDetails
            profileId={selectedProfileId}
            onBack={() => setActiveTab('matches')}
            onOpenConversation={openConversationAndMessages}
            onUpgradeClick={() => setActiveTab('subscription')}
          />
        );
      case 'search': return <Search onSearch={handleSearch} />;
      case 'shortlisted': return <Shortlisted onViewProfile={handleViewProfile} />;
      case 'viewed': return <ViewedProfiles onViewProfile={handleViewProfile} />;
      case 'visitors':
        return <Visitors setActiveTab={setActiveTab} />;
      case 'messages': return <Messages conversationToOpenId={conversationToOpenId} setActiveTab={setActiveTab} />;
      case 'contacts': return <ContactReveals />;
      case 'horoscope': return <HoroscopeMatch />;
      case 'ai-jathagam': return <AIJathagam />;
      case 'market': return <Market />;
      case 'subscription': return <Subscription />;
      case 'settings': return <Settings />;
      case 'profile': return <Profile onProfileUpdate={fetchProfileData} />;
      case 'help': return <Help />;
      case 'privacy': return <DashboardPrivacy />;
      case 'notifications': return <NotificationsPage notifications={notifications} onClearAll={clearAllNotifications} onMarkAllRead={markAllAsRead} />;
      default: return <Matches filter="all" searchQuery={searchQuery} onViewProfile={handleViewProfile} onOpenConversation={openConversationAndMessages} />;
    }
  };

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const displayCount = unreadCount > 10 ? '10+' : unreadCount;
  const SIDEBAR_ITEMS = [
    { key: "home", label: "Home" },
    { key: "profile", label: "My Profile" },
    { key: "matches", label: "Matches" },
    { key: "shortlisted", label: "Shortlisted" },
    { key: "visitors", label: "Visitors" },
    { key: "viewed", label: "Viewed Profiles" },
    { key: "messages", label: "Chat" },
    { key: "contacts", label: "Contact Reveals" },
    { key: "horoscope", label: "Horoscope Match", feature: "horoscopeMatching" },
    { key: "ai-jathagam", label: "AI Jathagam", feature: "aiJathagam" },
    { key: "market", label: "Market", feature: "marketAccess" },
    { key: "subscription", label: "Membership Plans" },
    { key: "settings", label: "Settings" },
    { key: "help", label: "Help & Support" },
    { key: "privacy", label: "Privacy Policy" },
  ];
  const filteredSuggestions = SIDEBAR_ITEMS.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper function to get icons for sidebar items
  const getIconForItem = (key) => {
    const iconMap = {
      home: <FaHome />,
      profile: <FaUser />,
      matches: <FaHeart />,
      shortlisted: <FaStar />,
      visitors: <FaUserFriends />,
      viewed: <FaEye />,
      messages: <FaEnvelope />,
      contacts: <FaPhone />,
      horoscope: <FaSun />,
      'ai-jathagam': <FaRobot />,
      market: <FaStore />,
      subscription: <FaGem />,
      settings: <FaCog />,
      help: <FaQuestionCircle />,
      privacy: <FaLock />
    };
    return iconMap[key] || <FaCircle className="text-xs" />;
  };
  return (
    <div className="flex h-screen bg-gray-50 font-sans text-gray-800 overflow-hidden">
      {/* ADDED: A nice soft chime notification sound (royalty-free) */}
      <audio ref={notificationAudioRef} src="/notification.mp3" preload="auto" />

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:shadow-xl flex flex-col h-full ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
      >
        {/* Sidebar Header */}
        <div className="p-6 flex items-center gap-3 border-b border-gray-200 bg-white shrink-0">
          <img
            src="/logo1.png" // replace with your actual file name
            alt="ST MatchZone Logo"
            className="w-18 h-8 object-contain"
          />

          <h1 className="text-xl font-semibold text-rose-600 font-serif tracking-wide -ml-4">
            ST MatchZone
          </h1>
        </div>

        {/* Scrollable Nav Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* Dynamic Profile Section */}
          {loadingProfile ? (
            <div className="p-6 flex flex-col items-center border-b border-gray-100 animate-pulse">
              <div className="w-24 h-24 rounded-full bg-gray-200"></div>
              <div className="mt-4 h-6 w-32 bg-gray-200 rounded"></div>
              <div className="mt-2 h-4 w-24 bg-gray-200 rounded"></div>
            </div>
          ) : user && profile ? (
            <div className="p-6 flex flex-col items-center border-b border-gray-100">
              <div
                className="relative group cursor-pointer"
                onClick={() => {
                  unlockAudio();
                  setActiveTab("profile");
                  setSidebarOpen(false);
                }}
              >
                <div className="relative">
                  <img
                    src={
                      profile.profilePhoto ||
                      (profile.photos && profile.photos.length > 0
                        ? profile.photos[0]
                        : null) ||
                      (profile.basicInfo?.gender === "Female"
                        ? "https://randomuser.me/api/portraits/women/85.jpg"
                        : "https://randomuser.me/api/portraits/men/85.jpg")
                    }
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        profile.basicInfo?.gender === "Female"
                          ? "https://randomuser.me/api/portraits/women/85.jpg"
                          : "https://randomuser.me/api/portraits/men/85.jpg";
                    }}
                    alt="Profile"
                    className="w-24 h-24 rounded-full border-4 border-rose-100 shadow-md transition-transform group-hover:scale-105 object-cover"
                  />
                  <div className="absolute bottom-1 right-1 bg-green-500 w-5 h-5 rounded-full border-2 border-white"></div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-full transition-all flex items-center justify-center">
                    <FaCamera className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-xl" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 flex flex-col items-center border-b border-gray-100">
              <div className="text-gray-400 text-sm">
                Profile not available
              </div>
            </div>
          )}

          <nav className="p-4 space-y-1.5">
            <SidebarItem
              icon={<FaHome />}
              label="Home"
              active={activeTab === "home"}
              onClick={() => {
                unlockAudio();
                setActiveTab("home");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaUser />}
              label="My Profile"
              active={activeTab === "profile"}
              onClick={() => {
                unlockAudio();
                setActiveTab("profile");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaHeart />}
              label="Matches"
              active={activeTab === "matches"}
              onClick={() => {
                unlockAudio();
                setFilter("all");        // ✅ RESET FILTER
                setSearchFilters(null); // ✅ CLEAR SEARCH
                setActiveTab("matches");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaStar />}
              label="Shortlisted"
              active={activeTab === "shortlisted"}
              onClick={() => {
                unlockAudio();
                setActiveTab("shortlisted");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaUserFriends />}
              label="Visitors"
              active={activeTab === "visitors"}
              onClick={() => {
                unlockAudio();
                setActiveTab("visitors");
                setSidebarOpen(false);
              }}
              badge="New"
            />

            <SidebarItem
              icon={<FaEye />}
              label="Viewed Profiles"
              active={activeTab === "viewed"}
              onClick={() => {
                unlockAudio();
                setActiveTab("viewed");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaEnvelope />}
              label="Chat"
              active={activeTab === "messages"}
              onClick={() => {
                unlockAudio();
                setActiveTab("messages");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaPhone />}
              label="Contact Reveals"
              active={activeTab === "contacts"}
              onClick={() => {
                unlockAudio();
                setActiveTab("contacts");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaSun />}
              label="Horoscope Match"
              active={activeTab === "horoscope"}
              locked={!features?.horoscopeMatching}
              onClick={() => {
                unlockAudio();
                handleFeatureClick("horoscope");
              }}
            />


            <SidebarItem
              icon={<FaRobot />}
              label="AI Jathagam"
              active={activeTab === "ai-jathagam"}
              locked={!features?.aiJathagam}
              onClick={() => {
                unlockAudio();
                handleFeatureClick("ai-jathagam");
              }}
            />

            <SidebarItem
              icon={<FaStore />}
              label="Market"
              active={activeTab === "market"}
              locked={!features?.marketAccess}
              onClick={() => {
                unlockAudio();
                handleFeatureClick("market");
              }}
            />

            <SidebarItem
              icon={<FaGem />}
              label="Membership Plans"
              active={activeTab === "subscription"}
              onClick={() => {
                unlockAudio();
                setActiveTab("subscription");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaCog />}
              label="Settings"
              active={activeTab === "settings"}
              onClick={() => {
                unlockAudio();
                setActiveTab("settings");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaQuestionCircle />}
              label="Help & Support"
              active={activeTab === "help"}
              onClick={() => {
                unlockAudio();
                setActiveTab("help");
                setSidebarOpen(false);
              }}
            />

            <SidebarItem
              icon={<FaLock />}
              label="Privacy Policy"
              active={activeTab === "privacy"}
              onClick={() => {
                unlockAudio();
                setActiveTab("privacy");
                setSidebarOpen(false);
              }}
            />
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-100 bg-white shrink-0">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center text-gray-600 hover:text-red-500 hover:bg-red-50 transition-all w-full px-4 py-3 rounded-xl font-medium"
          >
            <FaSignOutAlt className="mr-3" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm z-20 shrink-0">
          <div className="flex items-center justify-between px-4 py-3 lg:px-8 lg:py-4">
            <div className="flex items-center flex-1">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden mr-4 p-2 text-gray-600 hover:bg-gray-100 rounded-lg focus:outline-none"
              >
                <FaBars className="w-6 h-6" />
              </button>

              {/* Search Bar with Offers on Right + Clear Button */}
              <div className="relative flex items-center flex-1 max-w-3xl mx-auto hidden md:flex">
                <div className="relative flex-1 max-w-md">
                  <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />

                  <input
                    type="text"
                    placeholder="Search by ID, Name or Keyword..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                    className="w-full pl-10 pr-24 py-2.5 bg-gray-100 border-none rounded-full 
               focus:ring-2 focus:ring-rose-500 focus:bg-white outline-none 
               transition-all placeholder:text-gray-400 text-sm"
                  />

                  {/* Search meta info */}
                  {searchQuery && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-gray-400">
                      <span className="bg-white px-2 py-0.5 rounded-full shadow-sm">
                        {filteredSuggestions.length} results
                      </span>
                      <button
                        onClick={() => setSearchQuery('')}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <FaTimes className="text-xs" />
                      </button>
                    </div>
                  )}

                  {/* Keyboard shortcut hint */}
                  {!searchQuery && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:block">
                      <kbd className="px-2 py-1 text-xs font-semibold text-gray-500 bg-white rounded-md border border-gray-200 shadow-sm">
                        ⌘ K
                      </kbd>
                    </div>
                  )}

                  {/* Enhanced Suggestions Dropdown */}
                  {searchQuery && filteredSuggestions.length > 0 && (
                    <div className="absolute mt-2 w-full bg-white shadow-xl rounded-xl border border-gray-100 
                    z-50 max-h-80 overflow-y-auto animate-fadeIn">

                      {/* Search header */}
                      <div className="sticky top-0 bg-white px-4 py-2 border-b border-gray-100 
                      flex items-center justify-between text-xs text-gray-500">
                        <span>Quick Navigation</span>
                        <span className="flex items-center gap-1">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">↑</kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">↓</kbd>
                          <span>to navigate</span>
                        </span>
                      </div>

                      {/* Suggestions list */}
                      <div className="py-1">
                        {filteredSuggestions.map((item, index) => {
                          const isLocked = item.feature && !features?.[item.feature];

                          return (
                            <div
                              key={item.key}
                              onClick={() => {
                                if (!isLocked) {
                                  setActiveTab(item.key);
                                  setSearchQuery("");
                                }
                              }}
                              className={`px-4 py-3 flex items-center justify-between cursor-pointer 
                         transition-all duration-150 group
                         ${isLocked
                                  ? 'text-gray-400 bg-gray-50/50'
                                  : 'hover:bg-gradient-to-r hover:from-rose-50 hover:to-transparent'
                                }
                         ${index === 0 && !isLocked ? 'border-l-2 border-rose-500 bg-rose-50/30' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                {/* Icon with dynamic color */}
                                <span className={`text-base transition-colors duration-150
                  ${isLocked
                                    ? 'text-gray-400'
                                    : 'text-gray-500 group-hover:text-rose-500'
                                  }`}>
                                  {item.icon || getIconForItem(item.key)}
                                </span>

                                <div>
                                  <span className="font-medium">{item.label}</span>

                                  {/* Match preview */}
                                  {item.label.toLowerCase().includes(searchQuery.toLowerCase()) && (
                                    <span className="ml-2 text-xs text-rose-500">
                                      matches search
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                {isLocked ? (
                                  <span className="flex items-center gap-1 text-xs bg-yellow-50 
                                   text-yellow-700 px-2 py-1 rounded-full border border-yellow-200">
                                    <FaLock className="text-[10px]" />
                                    Premium
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 
                                   transition-opacity flex items-center gap-1">
                                    <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">↵</kbd>
                                    <span>Go to</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Search action footer */}
                      <div className="sticky bottom-0 bg-white px-4 py-2 border-t border-gray-100 
                      flex items-center justify-between text-xs text-gray-500">
                        <button
                          onClick={() => {
                            setActiveTab('search');
                            setSearchQuery('');
                          }}
                          className="flex items-center gap-2 text-rose-600 hover:text-rose-700 
                     transition-colors group"
                        >
                          <FaSearch className="text-xs group-hover:scale-110 transition-transform" />
                          <span>Advanced Search</span>
                        </button>

                        <span className="text-gray-400">
                          {filteredSuggestions.length} of {SIDEBAR_ITEMS.length} sections
                        </span>
                      </div>
                    </div>
                  )}

                  {/* No results state */}
                  {searchQuery && filteredSuggestions.length === 0 && (
                    <div className="absolute mt-2 w-full bg-white shadow-xl rounded-xl 
                    border border-gray-100 z-50 p-8 text-center animate-fadeIn">
                      <FaSearch className="text-4xl text-gray-200 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium mb-1">No results found</p>
                      <p className="text-sm text-gray-400 mb-4">
                        Try searching with different keywords
                      </p>
                      <button
                        onClick={() => {
                          setActiveTab('search');
                          setSearchQuery('');
                        }}
                        className="text-sm text-rose-600 hover:text-rose-700 font-medium 
                   transition-colors"
                      >
                        Use advanced search →
                      </button>
                    </div>
                  )}
                </div>

                {/* Offers Button on Right */}
                <button
                  onClick={() => {
                    unlockAudio();
                    setActiveTab("offers");
                  }}
                  className="ml-6 flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold px-6 py-2 rounded-full shadow-md hover:shadow-lg hover:from-yellow-500 hover:to-orange-600 transition-all"
                >
                  <FaGift className="text-lg" />
                  Offers
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-4 lg:space-x-6">
              <button
                onClick={() => {
                  unlockAudio();
                  setActiveTab("search");
                }}
                className="text-gray-600 hover:text-rose-600 transition-colors p-2 rounded-full hover:bg-gray-50"
                title="Filter Profiles"
              >
                <FaFilter className="text-xl" />
              </button>

              <button
                onClick={() => {
                  unlockAudio();
                  setActiveTab("search");
                }}
                className="md:hidden text-gray-600 hover:text-rose-600 transition-colors p-2 rounded-full hover:bg-gray-50"
              >
                <FaSearch className="text-xl" />
              </button>

              <button
                onClick={() => {
                  unlockAudio();
                  setActiveTab("shortlisted");
                }}
                className="text-gray-600 hover:text-red-500 transition-colors relative p-2 rounded-full hover:bg-gray-50"
                title="Shortlisted Profiles"
              >
                <FaHeart className="text-xl" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white"></span>
              </button>

              <button
                onClick={() => {
                  unlockAudio();
                  setActiveTab("messages");
                  setUnreadMessageCount(0); // Reset badge when opening chat
                }}
                className="text-gray-600 hover:text-rose-600 transition-colors relative p-2 rounded-full hover:bg-gray-50"
              >
                <FaEnvelope className="text-xl" />
                {unreadMessageCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs font-bold rounded-full ring-2 ring-white flex items-center justify-center text-[10px]">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              <div className="relative">
                <button
                  className="text-gray-600 hover:text-rose-600 transition-colors relative p-2 rounded-full hover:bg-gray-50 notification-bell-button"
                  onClick={() => {
                    unlockAudio();
                    setNotificationOpen(!notificationOpen);
                  }}
                >
                  <FaBell className="text-xl" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full ring-2 ring-white flex items-center justify-center text-[10px]">
                      {displayCount}
                    </span>
                  )}
                </button>
                {notificationOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 notification-dropdown-container">
                    <div className="p-4 border-b border-gray-100 font-bold text-gray-700 flex justify-between items-center">
                      <span>Notifications</span>
                      <div className="flex gap-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-rose-600 hover:underline"
                          >
                            Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button
                            onClick={clearAllNotifications}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {loadingNotifications ? (
                        <div className="p-8 text-center text-gray-400">
                          Loading...
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-400">
                          No notifications
                        </div>
                      ) : (
                        notifications.slice(0, 5).map((notif) => (
                          <div
                            key={notif._id}
                            className={`p-4 border-b border-gray-50 last:border-0 cursor-pointer transition-colors flex justify-between items-start
    ${!notif.isRead ? 'bg-rose-50' : 'hover:bg-gray-50'}
  `}
                            onClick={() => {
                              if (!notif.isRead) markAsRead(notif._id);
                            }}
                          >
                            <div className="flex-1">
                              <p className="text-sm text-gray-800 font-medium">
                                {notif.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(notif.createdAt).toLocaleString()}
                              </p>
                            </div>
                            {!notif.isRead && (
                              <FaCircle className="text-rose-600 text-xs mt-1 flex-shrink-0" />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="p-3 text-center border-t border-gray-100">
                        <button
                          onClick={() => {
                            setActiveTab("notifications");
                            setNotificationOpen(false);
                          }}
                          className="text-sm text-rose-600 font-bold hover:underline"
                        >
                          View All ({notifications.length})
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="relative group hidden md:block">
                <button
                  className="flex items-center space-x-3 focus:outline-none hover:bg-gray-50 px-3 py-2 rounded-xl transition-colors"
                  onClick={() => {
                    unlockAudio();
                    setActiveTab("profile");
                  }}
                >
                  {profile ? (
                    <>
                      <img
                        src={
                          profile.profilePhoto ||
                          (profile.photos && profile.photos.length > 0
                            ? profile.photos[0]
                            : null) ||
                          (profile.basicInfo?.gender === "Female"
                            ? "https://randomuser.me/api/portraits/women/85.jpg"
                            : "https://randomuser.me/api/portraits/men/85.jpg")
                        }
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            profile.basicInfo?.gender === "Female"
                              ? "https://randomuser.me/api/portraits/women/85.jpg"
                              : "https://randomuser.me/api/portraits/men/85.jpg";
                        }}
                        alt="User"
                        className="w-9 h-9 rounded-full border border-gray-200 shadow-sm object-cover"
                      />
                      <span className="font-semibold text-gray-700">
                        {profile.basicInfo?.firstName || "User"}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse"></div>
                      <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Content Scroll Area */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-4 lg:p-8">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

// Updated Notifications Page with Mark as Read & Clear All
const NotificationsPage = ({ notifications, onClearAll, onMarkAllRead }) => {
  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FaBell className="text-rose-600" />
            Notifications
          </h2>
          <div className="flex gap-3">
            {unreadCount > 0 && (
              <button onClick={onMarkAllRead} className="px-5 py-2 bg-rose-100 text-rose-600 rounded-xl font-bold hover:bg-rose-200 transition-colors flex items-center gap-2">
                <FaCheckCircle /> Mark All Read
              </button>
            )}
            {notifications.length > 0 && (
              <button onClick={onClearAll} className="px-5 py-2 bg-red-100 text-red-600 rounded-xl font-bold hover:bg-red-200 transition-colors flex items-center gap-2">
                <FaTrash /> Clear All
              </button>
            )}
          </div>
        </div>

        {notifications.length === 0 ? (
          <div className="text-center py-20">
            <FaBell className="text-6xl text-gray-200 mx-auto mb-4" />
            <p className="text-xl text-gray-500">No notifications yet</p>
            <p className="text-gray-400 mt-2">We'll let you know when something happens!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map(notif => (
              <div key={notif._id} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-lg font-medium text-gray-800">{notif.message}</p>
                  <p className="text-sm text-gray-500 mt-2">{new Date(notif.createdAt).toLocaleString()}</p>
                </div>
                {!notif.isRead && (
                  <FaCircle className="text-rose-600 text-xs mt-1" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const SidebarItem = ({
  icon,
  label,
  active,
  onClick,
  badge,
  locked = false,
}) => (
  <button
    onClick={locked ? null : onClick}
    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200
      ${locked
        ? "text-gray-400 cursor-not-allowed bg-gray-50"
        : active
          ? "bg-rose-50 text-rose-600 font-bold shadow-sm"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}
    `}
  >
    <div className="flex items-center gap-3">
      <span className="text-lg">{icon}</span>
      <span className="flex items-center gap-2">
        {label}

        {/* 🔒 Premium text */}
        {locked && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full
            bg-yellow-100 text-yellow-700 border border-yellow-300">
            Premium
          </span>
        )}
      </span>
    </div>

    {/* Right side icon */}
    {locked ? (
      <FaLock className="text-yellow-600 text-sm" />
    ) : (
      badge && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500 text-white">
          {badge}
        </span>
      )
    )}
  </button>
);


export default Dashboard;